import { Fragment } from 'preact';
import { unlockNarration } from '../audio/narrator';
import { N } from '../data/guide';
import { coversPlace } from '../live/darksites';
import { calendarPlace } from '../live/skycalendar';
import { panel, place, previewFrom, selection, type Panel } from '../state';
import { Sheet } from './chrome';
import { openGallery } from './galleryNav';
import { Icon } from './icons';
import { openIslam } from './IslamPanel';
import { openSizes } from './sizeBoards';

// More, the dock's fifth button: every part of Ahillah beyond the sky and tonight, one tap away, so nothing is found only
// by scrolling to the bottom of Tonight (the owner's choice of 23 October 2026, DECISIONS 108). Search finds them by name.

export interface MorePage {
  icon: string;
  label: string;
  sub: string;
  /** Words Search knows it by, besides its name: lower case, plain letters. */
  keys: string[];
  open: () => void;
  /** Across the whole row. */
  wide?: boolean;
  /** Offered only where it can help: dark-sky places are known only for some countries. */
  shown?: () => boolean;
}

const to = (p: Exclude<Panel, null>) => () => {
  selection.value = null;
  panel.value = p;
};

export const MORE: { title: string; pages: MorePage[] }[] = [
  {
    title: 'Learn',
    pages: [
      { icon: 'star', label: 'Lessons and quizzes', sub: 'Short lessons with tonight’s real sky', keys: ['learn', 'lessons', 'quiz', 'quizzes'], open: to('learn') },
      { icon: 'book', label: 'The stars in Islam', sub: 'What the Quran and the Sunnah say', keys: ['islam', 'quran', 'sunnah', 'hadith'], open: () => { selection.value = null; openIslam(); } },
      { icon: 'moon', label: 'Why Ahillah?', sub: 'The name, and the verse it comes from', keys: ['ahillah', 'name'], open: to('name'), wide: true },
    ],
  },
  {
    title: 'Plan a night',
    pages: [
      {
        icon: 'calendar', label: N.skyCalendar, sub: 'The year ahead in the sky', keys: ['calendar', 'events', 'year ahead', 'reminders'],
        // For the place being previewed, as from Tonight; otherwise the calendar starts from home.
        open: () => {
          if (previewFrom.peek()) calendarPlace.value = place.peek();
          to('calendar')();
        },
      },
      { icon: 'play', label: 'Guided tour', sub: 'A spoken tour of tonight’s sky', keys: ['tour', 'guided'], open: () => { unlockNarration(); to('tour')(); } },
      { icon: 'target', label: 'Sky Hunt', sub: 'Find five things yourself', keys: ['hunt', 'game', 'challenge'], open: to('hunt') },
      { icon: 'pin', label: 'Find darker skies', sub: 'Darker places within a drive', keys: ['dark', 'darker', 'where to go tonight'], open: to('go'), shown: () => coversPlace(place.value) },
    ],
  },
  { title: 'Yours', pages: [{ icon: 'nights', label: N.yourNights, sub: 'What you have seen, night by night', keys: ['nights', 'log', 'seen'], open: to('nights'), wide: true }] },
  {
    title: 'Look closer',
    pages: [
      { icon: 'gallery', label: N.gallery, sub: 'The finest photographs of space', keys: ['gallery', 'photos', 'pictures'], open: () => { selection.value = null; openGallery(); } },
      { icon: 'sizes', label: 'How big are they?', sub: 'Worlds side by side, true to size', keys: ['sizes', 'how big', 'compare'], open: () => { selection.value = null; openSizes(); } },
    ],
  },
  // Also first in Help (src/ui/FeedbackPanel.tsx).
  { title: 'Tell us', pages: [{ icon: 'message', label: 'Send feedback', sub: 'Something not right, or an idea', keys: ['feedback', 'problem', 'report', 'bug', 'idea', 'suggestion', 'tell us', 'contact'], open: to('feedback'), wide: true }] },
];

// As Search's own: accents and the marks within a word (Qur’an, Qurʾān) go, other punctuation parts words.
const plain = (s: string) => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[ʿʾ’'`]/g, '').replace(/[^a-z0-9 ]+/g, ' ').replace(/\s+/g, ' ').trim();

/** For Search: the pages whose name, or a word they are known by, begins with what was typed (three letters or more). */
export function findPages(typed: string): MorePage[] {
  const q = plain(typed);
  if (q.length < 3) return [];
  return MORE.flatMap((g) => g.pages).filter((p) => (!p.shown || p.shown()) && [plain(p.label), ...p.keys].some((k) => k.startsWith(q) || k.includes(` ${q}`)));
}

export function MorePanel() {
  return (
    <Sheet onClose={() => (panel.value = null)} size="tall" label={N.more}>
      <h2 class="sheet-title">{N.more}</h2>
      <p class="sheet-lead">Everything in Ahillah, in one place.</p>
      {MORE.map((g) => (
        <Fragment key={g.title}>
          <h3 class="section-title">{g.title}</h3>
          <div class="more-grid">
            {g.pages.filter((p) => !p.shown || p.shown()).map((p) => (
              <button type="button" key={p.label} class={p.wide ? 'more-tile wide' : 'more-tile'} onClick={p.open}>
                <span class="result-icon"><Icon name={p.icon} size={20} /></span>
                <span class="more-tile-text">
                  <span class="more-tile-label">{p.label}</span>
                  <span class="more-tile-sub">{p.sub}</span>
                </span>
              </button>
            ))}
          </div>
        </Fragment>
      ))}
    </Sheet>
  );
}
